import React from 'react';
import { Progress, Timeline, Alert, Card, Space, Skeleton } from 'antd';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  LoadingOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  ScanOutlined,
  RobotOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { ProgressInfo, formatEstimatedTime } from '../services/progressManager';
import StatusTag from './StatusTag';

interface ProgressDisplayFullProps {
  progress: ProgressInfo | null;
  loading?: boolean;
}

const ProgressDisplayFull: React.FC<ProgressDisplayFullProps> = ({ progress, loading }) => {
  // 获取阶段图标
  const getStageIcon = (stage: string, status: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      upload: <CloudUploadOutlined />,
      ocr: <ScanOutlined />,
      analysis: <RobotOutlined />,
      generation: <FileTextOutlined />,
    };

    const icon = iconMap[stage] || <ClockCircleOutlined />;

    if (status === 'processing') {
      return <LoadingOutlined spin />;
    } else if (status === 'completed') {
      return <CheckCircleOutlined />;
    } else if (status === 'failed') {
      return <CloseCircleOutlined />;
    }
    
    return icon;
  };

  // 获取阶段颜色
  const getStageColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'processing':
        return 'blue';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  // 计算总体进度
  const calculateOverallProgress = () => {
    if (!progress) return 0;
    
    // 根据当前阶段和进度计算总体进度
    const stageWeights: Record<string, number> = {
      upload: 0.2,      // 20%
      ocr: 0.3,         // 30%
      analyzing: 0.35,  // 35%
      generating: 0.15, // 15%
      completed: 1.0    // 100%
    };

    const stages = ['upload', 'ocr', 'analyzing', 'generating'];
    let totalProgress = 0;

    for (const stage of stages) {
      if (progress.stage === stage) {
        // 当前阶段的部分进度
        totalProgress += stageWeights[stage] * (progress.progress / 100);
        break;
      } else {
        // 已完成阶段
        const stageKey = stage === 'analyzing' ? 'analysis' : 
                        stage === 'generating' ? 'generation' : stage;
        const stageStatus = progress.details[stageKey as keyof typeof progress.details];
        if (stageStatus?.status === 'completed') {
          totalProgress += stageWeights[stage];
        }
      }
    }

    return Math.round(totalProgress * 100);
  };

  // 如果正在加载，显示骨架屏
  if (loading && !progress) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  // 如果没有进度信息
  if (!progress) {
    return null;
  }

  const overallProgress = calculateOverallProgress();

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* 整体进度条 */}
      <Card title="处理进度" size="small">
        <Progress 
          percent={overallProgress}
          status={progress.status === 'failed' ? 'exception' : 
                  progress.status === 'completed' ? 'success' : 'active'}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
          format={(percent) => (
            <span>
              {percent}%
              {progress.status === 'processing' && progress.estimated_time && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {formatEstimatedTime(progress.estimated_time)}
                </div>
              )}
            </span>
          )}
        />
        <div style={{ marginTop: '8px', color: '#666' }}>
          {progress.message}
        </div>
      </Card>

      {/* 阶段时间线 */}
      <Card title="处理步骤" size="small">
        <Timeline mode="left">
          {/* 文件上传 */}
          <Timeline.Item 
            color={getStageColor(progress.details.upload.status)}
            dot={getStageIcon('upload', progress.details.upload.status)}
          >
            <div>
              <strong>文件上传</strong>
              <span style={{ marginLeft: 8 }}>
                <StatusTag 
                  status={progress.details.upload.status} 
                  customText={progress.details.upload.status === 'processing' ? '上传中' : undefined}
                  size="small"
                />
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {progress.details.upload.message}
            </div>
            {progress.details.upload.status === 'processing' && progress.details.upload.progress > 0 && (
              <Progress 
                percent={progress.details.upload.progress} 
                size="small" 
                showInfo={false}
                style={{ marginTop: 8 }}
              />
            )}
          </Timeline.Item>

          {/* OCR识别 */}
          <Timeline.Item 
            color={getStageColor(progress.details.ocr.status)}
            dot={getStageIcon('ocr', progress.details.ocr.status)}
          >
            <div>
              <strong>文字识别</strong>
              <span style={{ marginLeft: 8 }}>
                <StatusTag 
                  status={progress.details.ocr.status} 
                  customText={progress.details.ocr.status === 'processing' ? '识别中' : undefined}
                  size="small"
                />
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {progress.details.ocr.message}
            </div>
            {progress.details.ocr.status === 'processing' && progress.details.ocr.progress > 0 && (
              <Progress 
                percent={progress.details.ocr.progress} 
                size="small" 
                showInfo={false}
                style={{ marginTop: 8 }}
              />
            )}
          </Timeline.Item>

          {/* AI分析 */}
          <Timeline.Item 
            color={getStageColor(progress.details.analysis.status)}
            dot={getStageIcon('analysis', progress.details.analysis.status)}
          >
            <div>
              <strong>AI分析</strong>
              <span style={{ marginLeft: 8 }}>
                <StatusTag 
                  status={progress.details.analysis.status} 
                  customText={progress.details.analysis.status === 'processing' ? '分析中' : undefined}
                  size="small"
                />
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {progress.details.analysis.message}
            </div>
            {progress.details.analysis.status === 'processing' && progress.details.analysis.progress > 0 && (
              <Progress 
                percent={progress.details.analysis.progress} 
                size="small" 
                showInfo={false}
                style={{ marginTop: 8 }}
              />
            )}
          </Timeline.Item>

          {/* 生成报告 */}
          <Timeline.Item 
            color={getStageColor(progress.details.generation.status)}
            dot={getStageIcon('generation', progress.details.generation.status)}
          >
            <div>
              <strong>生成报告</strong>
              <span style={{ marginLeft: 8 }}>
                <StatusTag 
                  status={progress.details.generation.status} 
                  customText={progress.details.generation.status === 'processing' ? '生成中' : undefined}
                  size="small"
                />
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {progress.details.generation.message}
            </div>
            {progress.details.generation.status === 'processing' && progress.details.generation.progress > 0 && (
              <Progress 
                percent={progress.details.generation.progress} 
                size="small" 
                showInfo={false}
                style={{ marginTop: 8 }}
              />
            )}
          </Timeline.Item>
        </Timeline>
      </Card>

      {/* 预计剩余时间 */}
      {progress.estimated_time && progress.status === 'processing' && (
        <Alert
          message={`预计剩余时间：${formatEstimatedTime(progress.estimated_time)}`}
          type="info"
          showIcon
          icon={<ClockCircleOutlined />}
        />
      )}

      {/* 失败提示 */}
      {progress.status === 'failed' && (
        <Alert
          message="处理失败"
          description={progress.message}
          type="error"
          showIcon
        />
      )}

      {/* 成功提示 */}
      {progress.status === 'completed' && (
        <Alert
          message="处理完成"
          description="作文批改已完成，可以查看批改结果了"
          type="success"
          showIcon
        />
      )}
    </Space>
  );
};

export default ProgressDisplayFull;